import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getFormUrl } from '@/utils/getFormUrl';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '美女直播平台精选',
    url: 'https://live-platform.example.com',
    description: '汇聚高颜值主播实时在线互动的直播平台推荐网站',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@live-platform.example.com',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const submitAddr = getFormUrl('联系表单');
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 美女直播平台精选"
        description="了解美女直播平台精选的品牌故事、口碑评价和联系方式。我们致力于为用户推荐最优质的美女直播平台和高颜值主播。"
        keywords="美女直播平台,关于我们,品牌介绍,联系方式"
        canonicalUrl="https://live-platform.example.com/about"
        schemaData={[orgSchema]}
      />

      <div className="pt-20 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              关于我们
            </h1>
            <p className="text-foreground-600 text-base max-w-2xl mb-10">
              致力于为用户推荐最优质的美女直播平台和高颜值主播
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20elegant%20office%20interior%20with%20warm%20golden%20lighting%2C%20creative%20team%20workspace%20with%20stylish%20furniture%20and%20plants%2C%20professional%20yet%20inviting%20atmosphere%2C%20brand%20story%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="美女直播平台精选 - 品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  美女直播平台精选成立于2023年，是国内领先的直播平台推荐与内容聚合网站。我们致力于为直播爱好者和高颜值主播之间搭建一座高效的连接桥梁。
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  我们的团队由资深的直播行业从业者和技术专家组成，深谙直播行业的发展趋势和用户需求。通过严格的筛选机制和专业的评测体系，我们为用户推荐最优质的美女直播平台和高颜值主播。
                </p>
                <p className="text-foreground-600 text-base leading-relaxed">
                  目前，我们已与超过50个优质直播平台建立合作关系，收录了500+位高颜值主播，每日为数万名用户提供精准的直播内容推荐服务。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                我们的优势
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">
                为什么选择美女直播平台精选
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '严格筛选', desc: '每位主播都经过严格的资质审核和内容评估，确保推荐质量' },
                { icon: 'ri-thumb-up-line', title: '真实评价', desc: '基于真实用户反馈的评分体系，帮助您做出明智选择' },
                { icon: 'ri-timer-flash-line', title: '实时更新', desc: '24小时实时更新主播状态，不错过任何精彩直播内容' },
                { icon: 'ri-customer-service-2-line', title: '贴心服务', desc: '专业的客服团队随时为您解答疑问，提供个性化推荐' },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 text-base mb-2">{item.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">
                来自真实用户的评价与反馈
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '王先生',
                  role: '资深直播观众',
                  content: '这个平台推荐真的很靠谱！通过他们的推荐，我找到了好几个喜欢的才艺主播，每天晚上的直播时间都变得特别充实。界面也很清爽，分类清晰。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '游戏爱好者',
                  content: '作为一个游戏迷，在美女直播平台精选上找到了很多技术超棒的游戏陪玩主播。不仅能学技术，看她们打游戏的过程也特别有趣。',
                  rating: 5,
                },
                {
                  name: '张先生',
                  role: '直播行业从业者',
                  content: '从行业角度来看，这个平台的推荐机制做得很专业。不是简单地追求流量，而是真正从用户需求出发做内容筛选，值得信赖。',
                  rating: 4,
                },
              ].map((review) => (
                <div key={review.name} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div>
                    <p className="font-heading font-semibold text-foreground-950 text-sm">{review.name}</p>
                    <p className="text-foreground-400 text-xs">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队将在24小时内回复您的咨询。
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">电子邮箱</p>
                      <p className="text-foreground-800 text-sm font-medium">contact@live-platform.example.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">客服电话</p>
                      <p className="text-foreground-800 text-sm font-medium">400-888-8888</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">公司地址</p>
                      <p className="text-foreground-800 text-sm font-medium">北京市朝阳区互联网创新园区A座15层</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6"
                >
                  <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-5">
                    发送消息
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                        您的姓名 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                        电子邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
                        留言内容 <span className="text-primary-600">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-accent-600 text-sm text-center bg-accent-50 py-2 px-3 rounded-lg">
                        消息已成功发送！我们会尽快回复您。
                      </p>
                    )}

                    {formStatus === 'error' && (
                      <p className="text-red-600 text-sm text-center bg-red-50 py-2 px-3 rounded-lg">
                        {formError}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}